import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { Header } from "@/components/layout/Header";
import { StepIndicator } from "@/components/layout/StepIndicator";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Bluetooth,
  BluetoothConnected,
  BluetoothSearching,
  RefreshCw,
  Check,
  X,
  Loader2,
  Activity,
  Heart,
  Droplets,
  Gauge,
  Thermometer,
  ArrowRight,
  Wifi,
  WifiOff,
  AlertCircle,
  Play
} from "lucide-react";

// Device configurations
const deviceConfigs = {
  glucometer: {
    name: "Glucometro",
    icon: Droplets,
    services: ["glicemia", "emoglobina_glicata"],
    mockReading: { value: 98, unit: "mg/dL" }
  },
  bloodPressure: {
    name: "Sfigmomanometro",
    icon: Gauge,
    services: ["pressione"],
    mockReading: { systolic: 120, diastolic: 80, pulse: 72, unit: "mmHg" }
  },
  oximeter: {
    name: "Ossimetro",
    icon: Activity,
    services: ["ossimetria"],
    mockReading: { spo2: 98, pulse: 72, unit: "%" }
  },
  ecg: {
    name: "ECG Portatile",
    icon: Heart,
    services: ["ecg"],
    mockReading: { heartRate: 72, rhythm: "Sinusale", unit: "bpm" }
  },
  thermometer: {
    name: "Termometro",
    icon: Thermometer,
    services: ["temperatura"],
    mockReading: { value: 36.5, unit: "°C" }
  },
  scale: {
    name: "Bilancia",
    icon: Gauge,
    services: ["peso"],
    mockReading: { weight: 75.5, bmi: 24.2, unit: "kg" }
  },
  analyzer: {
    name: "Analizzatore Ematico",
    icon: Droplets,
    services: ["emocromo", "colesterolo", "trigliceridi"],
    mockReading: {
      wbc: 7.2,
      rbc: 4.8,
      hgb: 14.5,
      cholesterol: 185,
      triglycerides: 120
    }
  },
  spirometer: {
    name: "Spirometro",
    icon: Activity,
    services: ["spirometria"],
    mockReading: { fev1: 3.5, fvc: 4.2, ratio: 83, unit: "L" }
  }
};

export const DeviceConnectionPage = () => {
  const navigate = useNavigate();
  const { selectedServices, deviceReadings, setDeviceReadings } = useAppContext();
  const [devices, setDevices] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [acquiringDevice, setAcquiringDevice] = useState(null);
  const [acquisitionProgress, setAcquisitionProgress] = useState(0);

  // Get required devices based on selected services
  useEffect(() => {
    const requiredDevices = [];
    Object.entries(deviceConfigs).forEach(([key, config]) => {
      const hasService = config.services.some(s => selectedServices.includes(s));
      if (hasService) {
        requiredDevices.push({
          id: key,
          ...config,
          status: "disconnected", // disconnected, connecting, connected, error
          matchedServices: config.services.filter(s => selectedServices.includes(s))
        });
      }
    });
    setDevices(requiredDevices);
  }, [selectedServices]);

  const handleScan = async () => {
    setIsScanning(true);
    
    // Simulate finding devices
    for (let i = 0; i < devices.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 800));
      setDevices(prev => prev.map((d, idx) => 
        idx === i ? { ...d, status: "connecting" } : d
      ));
      
      await new Promise(resolve => setTimeout(resolve, 600));
      setDevices(prev => prev.map((d, idx) => 
        idx === i ? { ...d, status: "connected" } : d
      ));
    }
    
    setIsScanning(false);
    toast.success("Tutti i dispositivi connessi!");
  };

  const acquireReading = async (device) => {
    setAcquiringDevice(device.id);
    setAcquisitionProgress(0);
    
    // Simulate acquisition progress
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 200));
      setAcquisitionProgress(i);
    }
    
    // Store mock reading
    setDeviceReadings(prev => ({
      ...prev,
      [device.id]: {
        device: device.name,
        services: device.matchedServices,
        reading: device.mockReading,
        timestamp: new Date().toISOString()
      }
    }));
    
    setAcquiringDevice(null);
    setAcquisitionProgress(0);
    
    toast.success(`Lettura acquisita: ${device.name}`);
  };

  const allConnected = devices.every(d => d.status === "connected");
  const allAcquired = devices.every(d => deviceReadings[d.id]);

  const handleContinue = () => {
    if (!allAcquired) {
      toast.error("Completa tutte le acquisizioni prima di continuare");
      return;
    }
    navigate("/results");
  };

  const getDeviceStatusIcon = (status) => {
    switch (status) {
      case "connected":
        return <BluetoothConnected className="h-5 w-5 text-success" />;
      case "connecting":
        return <BluetoothSearching className="h-5 w-5 text-info animate-pulse" />;
      case "error":
        return <X className="h-5 w-5 text-destructive" />;
      default:
        return <Bluetooth className="h-5 w-5 text-muted-foreground" />;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header title="Connessione Dispositivi" />
      <StepIndicator currentStep={3} />
      
      <main className="flex-1 px-4 pb-6 md:px-6">
        <div className="mx-auto max-w-4xl space-y-6">
          {/* Page header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                Connessione Dispositivi
              </h1>
              <p className="mt-1 text-muted-foreground">
                Collega i dispositivi medici via Bluetooth per acquisire i parametri
              </p>
            </div>
            <Button
              variant="bluetooth"
              size="lg"
              onClick={handleScan}
              disabled={isScanning || allConnected}
              className="gap-2"
            >
              {isScanning ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : allConnected ? (
                <Check className="h-5 w-5" />
              ) : (
                <BluetoothSearching className="h-5 w-5" />
              )}
              {isScanning ? "Ricerca..." : allConnected ? "Tutti Connessi" : "Cerca Dispositivi"}
            </Button>
          </div>

          {/* Bluetooth scanning animation */}
          {isScanning && (
            <Card className="border-info/30 bg-info/5">
              <CardContent className="py-8">
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="h-16 w-16 rounded-full bg-info/20 flex items-center justify-center">
                      <BluetoothSearching className="h-8 w-8 text-info" />
                    </div>
                    <div className="bluetooth-wave" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                    <div className="bluetooth-wave" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                    <div className="bluetooth-wave" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                  </div>
                  <p className="mt-4 font-medium text-info">Ricerca dispositivi in corso...</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Device list */}
          <ScrollArea className="h-[400px] md:h-[450px]">
            <div className="space-y-4 pr-4">
              {devices.map((device, index) => {
                const Icon = device.icon;
                const isAcquiring = acquiringDevice === device.id;
                const hasReading = !!deviceReadings[device.id];
                
                return (
                  <Card
                    key={device.id}
                    className={cn(
                      "device-card transition-all animate-fade-in-up",
                      hasReading && "border-success/50 bg-success/5"
                    )}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <CardContent className="p-4 md:p-6">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        {/* Device info */}
                        <div className="flex items-center gap-4 flex-1">
                          <div className={cn(
                            "p-3 rounded-xl transition-colors",
                            device.status === "connected" ? "bg-success/10" : "bg-muted"
                          )}>
                            <Icon className={cn(
                              "h-6 w-6",
                              device.status === "connected" ? "text-success" : "text-muted-foreground"
                            )} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{device.name}</h3>
                              {getDeviceStatusIcon(device.status)}
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {device.matchedServices.map(serviceId => (
                                <Badge key={serviceId} variant="outline" className="text-xs">
                                  {serviceId}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Action/Status */}
                        <div className="flex items-center gap-3">
                          {hasReading ? (
                            <div className="flex items-center gap-2 text-success">
                              <Check className="h-5 w-5" />
                              <span className="text-sm font-medium">Acquisito</span>
                            </div>
                          ) : device.status === "connected" ? (
                            <Button
                              variant="premium"
                              size="default"
                              onClick={() => acquireReading(device)}
                              disabled={isAcquiring}
                              className="gap-2"
                            >
                              {isAcquiring ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                              {isAcquiring ? "Acquisendo..." : "Acquisisci"}
                            </Button>
                          ) : device.status === "connecting" ? (
                            <Badge variant="secondary" className="gap-1 animate-pulse">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Connessione...
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="gap-1">
                              <WifiOff className="h-3 w-3" />
                              Non connesso
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Acquisition progress */}
                      {isAcquiring && (
                        <div className="mt-4">
                          <Progress value={acquisitionProgress} className="h-2" />
                          <p className="text-xs text-muted-foreground mt-1 text-center">
                            Acquisizione in corso... {acquisitionProgress}%
                          </p>
                        </div>
                      )}

                      {/* Reading preview */}
                      {hasReading && (
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-xs text-muted-foreground mb-2">Lettura acquisita:</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(deviceReadings[device.id].reading).map(([key, value]) => {
                              if (key === "unit") return null;
                              return (
                                <Badge key={key} className="bg-primary/10 text-primary border-primary/20">
                                  {key}: {value}{deviceReadings[device.id].reading.unit && ` ${deviceReadings[device.id].reading.unit}`}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>

          {/* Continue button */}
          <Card className="sticky bottom-4 shadow-lg border-2">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={allAcquired ? "default" : "secondary"}
                      className="text-sm"
                    >
                      {Object.keys(deviceReadings).length}/{devices.length} acquisiti
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {allAcquired 
                      ? "Tutte le letture sono state acquisite" 
                      : "Completa tutte le acquisizioni per continuare"
                    }
                  </p>
                </div>
                <Button
                  variant="premium"
                  size="lg"
                  onClick={handleContinue}
                  disabled={!allAcquired}
                  className="gap-2"
                >
                  Visualizza Risultati
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default DeviceConnectionPage;